// =============================================
// useContentAudits — hook dla 4. fazy pipeline'u (Sprawdzanie opublikowanej treści).
// Realtime przez Supabase channel + bulk audit + single re-audit.
// =============================================

import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import {
  auditNode,
  auditBulk,
  fetchLatestAuditsForMap,
  type ContentAuditResult,
  type ContentAuditOptions,
} from "@/services/contentAuditService";

export function useContentAudits(mapId: string) {
  const [audits, setAudits] = useState<Map<string, ContentAuditResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const { toast } = useToast();

  const fetchAudits = useCallback(async () => {
    if (!mapId) return;
    setLoading(true);
    try {
      const m = await fetchLatestAuditsForMap(mapId);
      setAudits(m);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  // Realtime — gdy nowy audit insert wpadnie, odśwież listę
  const { isLive } = useRealtimeSubscription({
    channel: `audits_${mapId || "none"}`,
    table: "content_audits",
    enabled: Boolean(mapId),
    onChange: fetchAudits,
  });

  const runSingle = useCallback(
    async (nodeId: string, options: ContentAuditOptions = {}) => {
      setLoading(true);
      try {
        const result = await auditNode(nodeId, options);
        setAudits((prev) => {
          const next = new Map(prev);
          next.set(nodeId, result);
          return next;
        });
        toast({
          title: `Audyt: ${result.audit_status}`,
          description: result.cached
            ? "Cache hit (treść unchanged od ostatniego audytu)"
            : `Score ${result.overall_score}/100, koszt $${result.cost_usd.toFixed(4)}`,
        });
        return result;
      } catch (err) {
        toast({
          title: "Audyt failed",
          description: err instanceof Error ? err.message : "Nieznany",
          variant: "destructive",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const runBulk = useCallback(
    async (nodeIds: string[], options: ContentAuditOptions = {}) => {
      if (nodeIds.length === 0) {
        toast({ title: "Brak nodów do audytu" });
        return;
      }
      setLoading(true);
      setBulkProgress({ current: 0, total: nodeIds.length });
      try {
        let done = 0;
        const { results, failed, totalCost } = await auditBulk(nodeIds, options, () => {
          done++;
          setBulkProgress({ current: done, total: nodeIds.length });
        });
        // Update state z wynikami
        setAudits((prev) => {
          const next = new Map(prev);
          for (const r of results) next.set(r.node_id, r);
          return next;
        });
        toast({
          title: "Audyt zakończony",
          description: `OK: ${results.length}, błędów: ${failed}, koszt: $${totalCost.toFixed(2)}`,
          variant: failed > nodeIds.length / 4 ? "destructive" : "default",
        });
      } catch (err) {
        toast({
          title: "Bulk audit failed",
          description: err instanceof Error ? err.message : "Nieznany",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setBulkProgress(null);
      }
    },
    [toast],
  );

  // Computed stats
  const passedCount = Array.from(audits.values()).filter((a) => a.audit_status === "passed").length;
  const warningCount = Array.from(audits.values()).filter((a) => a.audit_status === "warning").length;
  const failedCount = Array.from(audits.values()).filter((a) => a.audit_status === "failed").length;

  return {
    audits,
    loading,
    isLive,
    bulkProgress,
    passedCount,
    warningCount,
    failedCount,
    runSingle,
    runBulk,
    refresh: fetchAudits,
  };
}
